import React, { useEffect, useRef, forwardRef } from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  Stack,
  Dialog,
  DialogContent,
  IconButton,
  TextField,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import gsap from "gsap";
import CloseIcon from "@mui/icons-material/Close";
import StyledInput from "../../Components/ContactInput";
import BoxInput from "../../Components/BoxInput";
import { Homeimages, leadgenhero, mleadgenheros } from "../../assets";
import { useForm, Controller } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { toast } from 'react-toastify';
import { useMutation } from '@tanstack/react-query';
import { contactForm } from "../../api/services";



const schema: yup.ObjectSchema<Omit<LeadData, 'type'>> = yup.object({
  name: yup.string().required('Name is required'),
  email: yup
    .string()
    .email('Invalid email format')
    .required('Email is required'),
  phoneNumber: yup
    .string()
    .matches(
      /^[6-9]\d{9}$/,
      'Phone number must start with 6, 7, 8, or 9 and be 10 digits',
    )
    .required('Phone number is required'),
  message: yup.string().optional(),
  source: yup.string().optional(),
});

// ✅ UPDATED: Added source field to interface
interface LeadData {
  name: string;
  email: string;
  phoneNumber: string;
  message?: string;
  type?: string;
  source?: string;
}

const HeroSection: React.FC = () => {
  const [open, setOpen] = React.useState(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const cardRefs = useRef<HTMLDivElement[]>([]);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<LeadData>({
    resolver: yupResolver(schema),
  });

  const mutation = useMutation({
    mutationFn: (newFormData: LeadData) => contactForm(newFormData),
    onSuccess: () => {
      toast.success('Successfully submitted!');
      resetForm();
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Submission failed');
    },
  });

  const resetForm = () => {
    reset({
      name: '',
      email: '',
      phoneNumber: '',
      message: '',
    });
  };

  // ✅ UPDATED: Added source field with "leadgen webpage" value
  const onSubmit = (data: LeadData) => {
    mutation.mutate({ ...data, type: 'contact', source: 'leadgen webpage' });
  };

  //   useEffect(() => {
  //     // ENTRY ANIMATION
  //     gsap.from(cardRefs.current, {
  //       y: 80,
  //     //   opacity: 0,
  //       duration: 1.2,
  //       stagger: 0.2,
  //       ease: "power3.out",
  //     });

  //     // FLOATING LOOP
  //     cardRefs.current.forEach((card, i) => {
  //       gsap.to(card, {
  //         y: "-=20",
  //         duration: 2 + i,
  //         repeat: -1,
  //         yoyo: true,
  //         ease: "sine.inOut",
  //       });
  //     });
  //   }, []);

  return (
    <Box
      sx={{
        position: "relative",
        minHeight: { xs: "100vh", md: "100vh" },
        display: "flex",

        // ✅ FIX HERE
        alignItems: {
          xs: "start", // mobile → top
          md: "center",     // desktop → center (same as before)
        },

        overflow: "hidden",

        pt: {
          xs: 0,  // 🔥 add top spacing for mobile
          md: 4,
        },
      }}
    >
      {/* Background */}
      <Box
    component="video"
    autoPlay
    muted
    loop
    playsInline
    sx={{
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      objectFit: "cover",
      zIndex: 0,
       objectPosition: {
        xs:"center 50%",
        md:"center 40%"
       },
        transform: {
      xs: "translateY(27%)", // 🔥 move video up
      md: "none",
    },
    }}
  >
    <source
      src="https://dprstorage.b-cdn.net/RW/leadgen/leadgenvediio.mov" // 🔥 replace this
      type="video/mp4"
    />
  </Box>

  {/* 🌑 DARK OVERLAY */}
  <Box
    sx={{
      position: "absolute",
      inset: 0,
      background: `
        linear-gradient(
          rgba(5,10,20,0.6),
          rgba(5,10,20,0.85)
        )
      `,
      zIndex: 1,
    }}
  />

      {/* Overlay */}
      {/* <Box
        sx={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(120deg, rgba(0,0,0,0.45), rgba(15,99,165,0.35))",
          backdropFilter: "blur(2px)",
          zIndex: 1,
        }}
      /> */}

      {/* Content */}
      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 2 }}>
        <Grid container spacing={6} alignItems="center">

          {/* LEFT SIDE */}
          <Grid item xs={12} md={6} lg={6}
           
          >
            <Stack spacing={3}
             sx={{
              pt: {
                xs: 12,  // 🔥 add top spacing for mobile
                md: "auto",
              },
            }}
            >

              <Box>
                <Typography
                  variant="h1"
                  sx={{
                    fontWeight: 700,
                    lineHeight: 1,
                    fontSize: { xs: "2.5rem", md: "3.5rem", lg: "auto" },
                    color: "#fff",

                    textAlign: { xs: "center", md: "start" },
                  }}
                >
                  Close High-Value Deals.

                </Typography>

                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: "2.5rem", md: "3.5rem", lg: "3rem" },
                    background:
                      "linear-gradient(90deg, #ffffff, #fafafa)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    fontFamily: '"Playfair Display", serif !important',
                    textAlign: { xs: "center", md: "start" },
                  }}
                >
                  Without Chasing
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    mt: 2,
                    color: "#fff",
                    fontSize: "1.1rem",
                    maxWidth: 500,
                    lineHeight: 1.7,
                    textAlign: { xs: "center", md: "start" },
                  }}
                >
                  Built from real ₹Crore level sales experience not theory, not scripts. A proven system to attract, influence, and close premium clients effortlessly.
                </Typography>
              </Box>

              {/* Glass Card */}
              {/* <Box
                sx={{
                  p: 3,
                  borderRadius: "20px",
                  backdropFilter: "blur(14px)",
                  background: "rgba(255,255,255,0.06)",
                  border: "1px solid rgba(255,255,255,0.12)",
                  maxWidth: 400,
                }}
              >
                <Stack spacing={2}>
                  {[
                    "15+ Years in High-Ticket Sales",
                    "₹100Cr+ Sales Conversations",
                    "Trusted by Premium Clients",
                  ].map((text, i) => (
                    <Stack direction="row" spacing={1} key={i}>
                      <CheckCircleIcon sx={{ color: "#0f63a5" }} />
                      <Typography variant="body2" sx={{ color: "#ddd", fontSize: "0.9rem" }}>
                        {text}
                      </Typography>
                    </Stack>
                  ))}

                  <Box
                    sx={{
                      borderTop: "1px solid rgba(255,255,255,0.1)",
                      pt: 1,
                    }}
                  >
                    <Typography variant="body2" sx={{ mt: 1, color: "#888" }}>
                      Real Systems. Real Results.
                    </Typography>
                  </Box>
                </Stack>
              </Box> */}

              {/* CTA */}
              <Button
                onClick={handleOpen}
                variant="contained"
                endIcon={<ArrowForwardIcon />}
                sx={{
                  width: { xs: "100%", md: "fit-content" },
                  px: 4,
                  py: 1.5,
                  borderRadius: "30px",
                  background:
                    "linear-gradient(90deg, #0f63a5, #42a5f5)",
                  textTransform: "none",
                  fontWeight: 600,
                  boxShadow: "0 8px 30px rgba(15,99,165,0.4)",

                  "&:hover": {
                    transform: "translateY(-2px)",
                  },
                }}
              >
                Book Private Strategy Call
              </Button>
            </Stack>
          </Grid>

          {/* RIGHT SIDE */}
          {/* <Grid
            item
            xs={12}
            md={6}
            sx={{
              position: "relative",
              height: { xs: "auto", md: 650 },
              minHeight: { xs: 400, md: "auto" },
              display: "flex",
              alignItems: "center",
              justifyContent: { xs: "center", md: "start" },
            }}
          >
            <Box
              sx={{
                position: "relative",

                // 🔥 MOBILE STACK FIX
                display: { xs: "flex", md: "block" },
                flexDirection: { xs: "column", md: "unset" },
                alignItems: { xs: "center", md: "unset" },

                width: "100%",
                height: "100%",
              }}
            >
              <FloatingCard
                top="10%"
                right="10%"
                title="3 Cr+"
                subtitle="Deals Closed in Last 12 Months"
              />

              <FloatingCard
                top="35%"
                right="20%"
                title="10X"
                subtitle="Conversion Growth for Clients"
              />

              <FloatingCard
                top="60%"
                right="30%"
                title="100%"
                subtitle="Premium Client Satisfaction"
              />
            </Box>
          </Grid> */}
        </Grid>
      </Container>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        BackdropProps={{
          sx: {
            background: "rgba(0,0,0,0.6)",
            backdropFilter: "blur(6px)",
          },
        }}
        PaperProps={{
          sx: {
            borderRadius: "24px",
            background: "rgba(255,255,255,0.08)",
            backdropFilter: "blur(25px)",
            border: "1px solid rgba(255,255,255,0.2)",
            boxShadow: `
        0 30px 80px rgba(0,0,0,0.6),
        inset 0 1px 0 rgba(255,255,255,0.2)
      `,
            overflow: "hidden",
          },
        }}
      >
        <DialogContent sx={{ position: "relative", p: 5 }}>

          {/* ✅ WATERMARK (FIXED) */}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              backgroundImage: `url(${Homeimages.rwsignlogo})`,
              backgroundRepeat: "no-repeat",
              backgroundPosition: "center",
              backgroundSize: "220px",
              opacity: 0.06,
              filter: "grayscale(100%)",
              pointerEvents: "none",
              zIndex: 0,
            }}
          />

          {/* CONTENT WRAPPER */}
          <Box sx={{ position: "relative", zIndex: 2 }}>

            {/* CLOSE BUTTON */}
            <IconButton
              onClick={handleClose}
              sx={{
                position: "absolute",
                top: 0,
                right: 5,

                width: 40,
                height: 40,

                background: "rgba(255,255,255,0.15)",
                backdropFilter: "blur(10px)",

                border: "1px solid rgba(255,255,255,0.2)",
                color: "#fff",

                transition: "0.3s",

                "&:hover": {
                  background: "rgba(255,255,255,0.3)",
                  transform: "rotate(90deg) scale(1.1)",
                },
              }}
            >
              <CloseIcon />
            </IconButton>
            {/* TITLE */}
            <Typography
              variant="h5"
              sx={{
                color: "#fff",
                textAlign: "center",
                mb: 4,

              }}
            >
              Apply for Private Access
            </Typography>

            {/* FORM */}
            <Box
              onSubmit={handleSubmit(onSubmit)}
              component="form"
              display="flex" flexDirection="column" gap={3}>


              {/* Name */}
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <BoxInput
                    placeholder="Enter your full name"
                    {...field}
                    fullWidth

                    error={!!errors.name}
                    helperText={errors.name?.message}
                  />
                )}
              />
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <BoxInput
                    placeholder="Enter your email address"
                    {...field}
                    fullWidth

                    error={!!errors.email}
                    helperText={errors.email?.message}
                  />
                )}
              />

              {/* PHONE */}
              <Controller
                name="phoneNumber"
                control={control}
                render={({ field }) => (
                  <BoxInput
                    placeholder="Enter your phone number"
                    {...field}
                    fullWidth

                    error={!!errors.phoneNumber}
                    helperText={errors.phoneNumber?.message}
                  />
                )}
              />

              {/* MESSAGE */}
              <Controller
                name="message"
                control={control}
                render={({ field }) => (
                  <BoxInput
                    placeholder="Enter your message"
                    {...field}
                    multiline
                    rows={3}
                    fullWidth

                  />
                )}
              />



              <Button
                type="submit"
                sx={{
                  py: 1.5,
                  borderRadius: "30px",
                  background: "linear-gradient(135deg, #0f63a5, #42a5f5)",
                  color: "#fff",
                }}
              >
                Apply Now →
              </Button>

            </Box>

          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default HeroSection;


// 🔥 Floating Card Component
type FloatingCardProps = {
  top: string;
  right: string;
  title: string;
  subtitle: string;
};

// ✅ TYPE DEFINITION (IMPORTANT)

// ✅ COMPONENT
const FloatingCard: React.FC<FloatingCardProps> = ({
  top,
  right,
  title,
  subtitle,
}) => {
  return (
    <Box
      sx={{
        position: { xs: "relative", md: "absolute" }, // 🔥 KEY FIX

        // desktop only
        top: { md: top },
        right: { md: right },

        // mobile layout
        width: 280,
        mx: "auto",
        mb: { xs: 3, md: 0 },

        p: 3,
        borderRadius: "20px",
        backdropFilter: "blur(18px)",
        background: "rgba(255,255,255,0.06)",
        border: "1px solid rgba(255,255,255,0.12)",
        borderLeft: "3px solid #0f63a5",
        boxShadow: "0 10px 40px rgba(0,0,0,0.3)",
        textAlign: { xs: "center", md: "start" }
      }}
    >
      <Typography
        variant="h4"
        sx={{
          fontWeight: 700,
          color: "#fff",
          fontFamily: '"Playfair Display", serif',
        }}
      >
        {title}
      </Typography>

      <Typography
        variant="body2"
        sx={{
          color: "#ddd",
          mt: 1
        }}>
        {subtitle}
      </Typography>

    </Box>

  );
};